import { OrganizationMember } from '@tinct/core-api-models';

export type MemberDirectoryState = Pick<
  OrganizationMember,
  'scimManaged' | 'roleManaged' | 'status'
>;

export const MANAGED_BY_DIRECTORY_REASON =
  'Manage this person in your identity provider';
export const ROLE_SET_BY_DIRECTORY_REASON =
  'Role set by the directory group mapping';

/** Why each row action is unavailable, `null` when it is available. */
export type MemberActionRestrictions = {
  /** Which of suspend or unsuspend the row offers, given the member's status. */
  suspension: 'suspend' | 'unsuspend';
  suspensionDisabledReason: string | null;
  removeDisabledReason: string | null;
  changeRoleDisabledReason: string | null;
};

/**
 * What the directory leaves an admin to do with a member. While SCIM is switched on, the directory
 * owns the life cycle of the members it manages (suspend, unsuspend, remove happen in the identity
 * provider), and a group mapping owns the role of the members it covers. Both flags outlive the
 * switch (a SCIM record, a mapped group), but the API only refuses (409, 5207 / 5208) while it is
 * on: switched off, nothing is restricted. `null` flags (a viewer who may not read the SCIM
 * settings) restrict nothing either: the API remains the gate.
 */
export const getMemberActionRestrictions = (
  { scimManaged, roleManaged, status }: MemberDirectoryState,
  /** The workspace's stored SCIM switch (`organization.scimStatus === 'enabled'`). */
  scimEnabled: boolean,
): MemberActionRestrictions => {
  const lifeCycleReason = scimEnabled && scimManaged ? MANAGED_BY_DIRECTORY_REASON : null;
  return {
    suspension: status === 'suspended' ? 'unsuspend' : 'suspend',
    suspensionDisabledReason: lifeCycleReason,
    removeDisabledReason: lifeCycleReason,
    changeRoleDisabledReason:
      scimEnabled && roleManaged ? ROLE_SET_BY_DIRECTORY_REASON : null,
  };
};

export type MemberStatusDisplay = {
  label: string;
  /** Tailwind class of the status dot. */
  dotClassName: string;
};

const STATUS_DISPLAYS: Record<string, MemberStatusDisplay> = {
  accepted: { label: 'Active', dotClassName: 'bg-emerald-400' },
  invited: { label: 'Pending', dotClassName: 'bg-amber-400' },
  suspended: { label: 'Suspended', dotClassName: 'bg-rose-500' },
};

/**
 * How a member's status reads. A member the directory deactivated is `suspended` for the API, like
 * one an admin suspended by hand, but only the directory can bring them back: they read
 * *Deactivated by directory*.
 */
export const getMemberStatusDisplay = ({
  status,
  scimManaged,
}: Pick<OrganizationMember, 'status' | 'scimManaged'>): MemberStatusDisplay | null => {
  if (!status) return null;
  if (status === 'suspended' && scimManaged) {
    return { label: 'Deactivated by directory', dotClassName: 'bg-slate-400' };
  }
  return (
    STATUS_DISPLAYS[status] ?? { label: status.toLowerCase(), dotClassName: 'bg-gray-400' }
  );
};
